import { TypeNotification, useGlobalStore } from '@/store/globalStore';
import { useTokenStore } from '@/store/tokenStore';
import { UseStoreUser } from '@/store/userStore';
import { User } from '@/types/user';
import habService from '@/services/habService';
import { getPublicRuntimeConfig } from '@/utils/configs';
import { unixTsNow } from '@/utils/unix';
import { getRealmFromUrl } from '@/utils/url';
import {
  hasHubRoleFromPayload,
  type AccessTokenPayload,
} from '@/utils/token';
import Keycloak from 'keycloak-js';

const keycloakInstance = new Keycloak({
  url: `${getPublicRuntimeConfig('VUE_APP_KEYCLOAK_PATH')}`,
  realm: getRealmFromUrl(),
  clientId: `${getPublicRuntimeConfig('VUE_APP_CLIENT_ID')}`,
});

const WINDOW_TOKEN_KEY = '__HUB_ACCESS_TOKEN__';

/** Parse access_token from URL hash or query (Keycloak may put it there on redirect). */
export function getTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash?.replace(/^#/, '') || '';
  const search = window.location.search?.replace(/^\?/, '') || '';
  const params = new URLSearchParams(hash || search);
  return params.get('access_token');
}

/** Token captured right before app mount so the router guard can read it (avoids store/timing issues). */
let savedAccessTokenForGuard: string | null = null;
export function getSavedAccessToken(): string | null {
  if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>)[WINDOW_TOKEN_KEY]) {
    return (window as unknown as Record<string, unknown>)[WINDOW_TOKEN_KEY] as string;
  }
  return savedAccessTokenForGuard;
}
export function clearSavedAccessToken(): void {
  savedAccessTokenForGuard = null;
  if (typeof window !== 'undefined') {
    delete (window as unknown as Record<string, unknown>)[WINDOW_TOKEN_KEY];
  }
}

/**
 * Helper to safely get nested properties from keycloak instance
 */
const get = <T>(
  obj: unknown,
  path: string,
  defaultValue: T | null = null
): T | null => {
  const keys = path.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let result: any = obj;
  for (const key of keys) {
    if (result == null) return defaultValue;
    result = result[key];
  }
  return result ?? defaultValue;
};
interface CallbackOneParam<T1 = void, T2 = void> {
  (param1: T1): T2;
}
/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
const Login = (onAuthenticatedCallback: CallbackOneParam) => {
  const user: User = {};
  const userStore = UseStoreUser();
  const globalStore = useGlobalStore();
  const activityStore = useTokenStore();
  keycloakInstance
    .init({ onLoad: 'login-required' })
    .then(async function (authenticated) {
      if (authenticated) {
        activityStore.setToken(keycloakInstance?.token || '');
        activityStore.setTokenExpiry(
          get(keycloakInstance, 'idTokenParsed.exp', 0) as number
        );
      }

      activityStore.registerRefreshCallback(() =>
        isTokenExpired() ? refreshToken() : Promise.resolve(false)
      );

      if (authenticated) {
        const parsed = keycloakInstance.tokenParsed as {
          preferred_username?: string;
          sub?: string;
          given_name?: string;
          family_name?: string;
        } | undefined;
        user.username = parsed?.preferred_username ?? '';
        user.id = parsed?.sub ?? '';
        user.firstName = parsed?.given_name ?? '';
        user.lastName = parsed?.family_name ?? '';
        userStore.loadUser(user);
        habService.loadUserRolesFromToken();

        const payload = keycloakInstance.tokenParsed as AccessTokenPayload | undefined;
        if (
          payload &&
          typeof payload === 'object' &&
          'resource_access' in payload &&
          !hasHubRoleFromPayload(payload)
        ) {
          const base = (typeof process !== 'undefined' && process.env?.BASE_URL) || '/';
          const path = base.replace(/\/?$/, '') + '/acces-refuse';
          if (typeof window !== 'undefined') {
            window.history.replaceState(null, '', path);
          }
        }

        const setTokenAndMount = (token: string) => {
          savedAccessTokenForGuard = token || null;
          if (typeof window !== 'undefined' && token) {
            (window as unknown as Record<string, unknown>)[WINDOW_TOKEN_KEY] =
              token;
          }
          activityStore.setUserAuth({
            access_token: token,
            refresh_token: keycloakInstance.refreshToken ?? '',
            id_token: keycloakInstance.idToken ?? '',
            expiry: keycloakInstance.idTokenParsed?.exp?.toString() || '',
          });
          onAuthenticatedCallback();
        };

        const tryMount = (attempt = 0) => {
          let accessToken = keycloakInstance.token ?? '';
          if (!accessToken) {
            const fromUrl = getTokenFromUrl();
            if (fromUrl) accessToken = fromUrl;
          }
          if (accessToken) {
            setTokenAndMount(accessToken);
            return;
          }
          if (attempt < 6) {
            setTimeout(() => tryMount(attempt + 1), 50 * (attempt + 1));
          } else {
            setTokenAndMount('');
          }
        };
        tryMount();
      } else {
        alert('non authenticated');
      }

      const profile = await keycloakInstance.loadUserInfo();

      const userAuth = {
        access_token: keycloakInstance.token ?? '',
        refresh_token: keycloakInstance.refreshToken ?? '',
        id_token: keycloakInstance.idToken ?? '',
        expiry: keycloakInstance.idTokenParsed?.exp?.toString() || '',
      };

      activityStore.setUserProfile(profile as Record<string, unknown>);
      activityStore.setUserAuth(userAuth);
      activityStore.setIsUserLogged(true);

      const username =
        (get(keycloakInstance, 'tokenParsed.preferred_username') as string) ||
        get(keycloakInstance, 'idTokenParsed.preferred_username') ||
        get(keycloakInstance, 'tokenParsed.email') ||
        null;
      const auth_time = get(keycloakInstance, 'tokenParsed.auth_time', null);
      const iat = get(keycloakInstance, 'idTokenParsed.iat', null);
      const exp = get(keycloakInstance, 'idTokenParsed.exp', null);
      const refreshExpToken = get(
        keycloakInstance,
        'refreshTokenParsed.exp',
        null
      );

      void activityStore.startTokenActivity(
        username,
        auth_time,
        iat,
        exp,
        refreshExpToken,
        true
      );

      try {
        const profile = await keycloakInstance.loadUserProfile();

        const userProfile: any = profile;
        user.username = profile.username;
        user.firstName = profile.firstName;
        user.lastName = profile.lastName;
        user.id = profile.id;
        user.apps = userProfile.attributes;
        userStore.loadUser(user);

        habService.loadUserRolesFromToken();
        if (userStore.hasAnyHubRole) {
          await userStore.fetchUserData();
        }
      } catch (error) {
        globalStore.showNotification(
          TypeNotification.ERROR,
          'Failed to load user profile.',
          'Failed',
          2500
        );
      }
    })
    .catch(e => {
      globalStore.showNotification(
        TypeNotification.ERROR,
        `Keycloak init exception ${e}`,
        'Exception',
        2500
      );
    });
};

const logOut = async () => {
  const activityStore = useTokenStore();
  keycloakInstance.logout().then(() => {
    activityStore.setIsUserLogged(false);
    sessionStorage.clear();
    activityStore.clearAuthData();
    clearSavedAccessToken();
  });
};

const refreshToken = async () => {
  const userStore = useTokenStore();

  if (keycloakInstance) {
    try {
      const refreshed = await keycloakInstance.updateToken(30);
      if (refreshed) {
        const idTokenExpiry = get(keycloakInstance, 'idTokenParsed.exp', 0);
        userStore.setTokenExpiry(idTokenExpiry as number);
        userStore.setTokenRefreshExpiry(
          get(
            keycloakInstance,
            'refreshTokenParsed.exp',
            idTokenExpiry
          ) as number
        );
        userStore.setToken(keycloakInstance?.token || '');
        userStore.setUserAuth({
          access_token: keycloakInstance.token ?? '',
          refresh_token: keycloakInstance.refreshToken ?? '',
          id_token: keycloakInstance.idToken ?? '',
          expiry: String(idTokenExpiry),
        });

        // Update tokenActivityStore after successful token refresh
        const tokenActivityStore = useTokenStore();
        const username =
          (get(keycloakInstance, 'tokenParsed.preferred_username') as string) ||
          null;
        const auth_time = get(keycloakInstance, 'tokenParsed.auth_time', null);
        const iat = get(keycloakInstance, 'idTokenParsed.iat', null);
        const exp = get(keycloakInstance, 'idTokenParsed.exp', null);
        const refreshExp = get(
          keycloakInstance,
          'refreshTokenParsed.exp',
          null
        );

        void tokenActivityStore.startTokenActivity(
          username,
          auth_time,
          iat,
          exp,
          refreshExp,
          false
        );

        return true;
      }
      return false;
    } catch (error) {
      alert(`Keycloak init exception ${error}`);
      return false;
    }
  }
  return false;
};

/**
 * Checks if the access token is expired
 * @param leewaySeconds - Optional buffer time in seconds before actual expiration
 * @returns true if token is expired or will expire within leeway period
 */
const isTokenExpired = (leewaySeconds = 0): boolean => {
  if (!keycloakInstance) return true;

  const exp = get(keycloakInstance, 'idTokenParsed.exp', 0) as number;
  if (!exp) return true;

  const now = Math.floor(Date.now() / 1000);
  return exp <= now + leewaySeconds;
};

/**
   * Attempts automatic token refresh based on user activity.
   * Only refreshes if there was recent user activity within the valid session range.
   * This ensures idle users are logged out after SSO Session Idle timeout.
  /**
   * Attempts automatic token refresh based on user activity.
   * Only refreshes if there was recent user activity within the valid session range.
   * This ensures idle users are logged out after SSO Session Idle timeout.
   * @returns Promise<boolean> true if refresh was attempted, false if no recent activity
   */
const attemptAutomaticRefresh = async (): Promise<boolean> => {
  try {
    const tokenActivityStore = useTokenStore();

    const sessionStart = tokenActivityStore.sessionStartTime ?? null;
    const lastActivityTs = tokenActivityStore.lastActivity ?? null;
    const lastAutoRefreshTs = tokenActivityStore.lastAutoRefresh ?? null;
    const unixNow = unixTsNow();

    // Check if last activity is within valid range (after session start, before now)
    const lastActivityIsInRange =
      sessionStart !== null &&
      lastActivityTs !== null &&
      sessionStart < lastActivityTs &&
      lastActivityTs < unixNow;

    // Check if last auto-refresh is within valid range
    const lastAutoRefreshIsInRange =
      lastAutoRefreshTs !== null &&
      sessionStart !== null &&
      sessionStart < lastAutoRefreshTs &&
      lastAutoRefreshTs < unixNow;

    // Only refresh if there was recent activity or recent auto-refresh
    if (lastActivityIsInRange || lastAutoRefreshIsInRange) {
      const refreshed = await refreshToken();
      if (refreshed && !lastAutoRefreshTs) {
        const iat = get(keycloakInstance, 'tokenParsed.iat', null);
        tokenActivityStore.updateLastAutoRefresh(iat);
      }
      return true;
    }

    return false;
  } catch {
    console.error('Automatic token refresh failed');
    return false;
  }
};

keycloakInstance.onTokenExpired = async () => {
  try {
    await attemptAutomaticRefresh();
  } catch {
    console.error('Automatic token refresh failed on token expiration');
  }
};

const changePassword = async () => {
  await keycloakInstance.updateToken(-1).then(async result => {
    if (keycloakInstance.authenticated) {
      await keycloakInstance.login({
        action: 'UPDATE_PASSWORD',
      });
    }
  });
};

const token = () => keycloakInstance.token;

const KeycloakService = {
  CallLogin: Login,
  CallLogout: logOut,
  CallUpdate: attemptAutomaticRefresh,
  CallPassword: changePassword,
  CallGetToken: token,
  currentInstance: keycloakInstance,
  isTokenExpired,
  attemptAutomaticRefresh,
  token,
  logOut,
};

export default KeycloakService;
